import { formatOrderRef } from '@misumi/shared'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Checkbox } from '../../components/ui/Checkbox'
import { Chip } from '../../components/ui/Chip'
import { Tooltip } from '../../components/ui/Tooltip'
import { useAnchoredPopover } from '../../components/ui/useAnchoredPopover'
import type { components } from '../../lib/api/schema.gen'
import { PartConfiguratorShell } from '../parts/components/PartConfiguratorShell'
import { PartRevBand } from '../parts/components/PartRevBand'
import type {
  PartConfiguratorFormState,
  PartConfiguratorSaveOutcome,
  PartConfiguratorSaveSeam,
} from '../parts/components/part-configurator-save'
import { formatRequirementsDetail } from '../parts/format'
import { formatProcessLabel } from '../parts/process-label'
import { PartThumbnail } from './PartThumbnail'
import { CART_MAX_LINE_QUANTITY } from './cart-limits'
import {
  CART_CONFIGURATOR_CAPABILITIES,
  type CartLineConfiguratorService,
  buildCartLineSpecificationPatch,
  defaultCartLineConfiguratorService,
  findCartLineSpecification,
  formatCartLineSpecificationSummary,
  fromCartLine,
} from './cart-line-configurator'
import { splitPartIdDisplay } from './cart-line-display'
import { type CartLineEditAction, resolveCartLineEditAction } from './cart-line-edit-action'
import { formatMoney } from './cart-money'
import { deriveLineReadiness, isDfmFail, isQuantityEditable } from './cart-readiness'
import { deriveLineDateDisplay } from './ready-earlier'

type LineItem = components['schemas']['LineItem']
type CartLinePresentation = components['schemas']['CartLinePresentation']
type CartLineSpecification = components['schemas']['CartLineSpecification']
type CartViewWithLineSpecifications = components['schemas']['CartViewWithLineSpecifications']
type ShipMode = components['schemas']['ShipMode']

/** Single source of truth for why a locked line's quantity can't be edited —
 * shown visually in the Tooltip on the "Locked to quote" chip AND exposed via
 * `aria-describedby` on the disabled quantity input, so assistive-tech users
 * get the same explanation the sighted Tooltip gives (WCAG F1). */
const LOCKED_QUANTITY_REASON =
  "This line's price is locked to an accepted quote — quantity can't be changed here."

/** Why a DFM-fail line shows a review chip (UID-76) — the part failed the
 * manufacturability check (Instant-Quoter, UID-49) and must be quoted manually,
 * distinct from a benign not-yet-quoted line that also reads "quote required". */
const DFM_FAIL_REASON =
  'This part failed the manufacturability (DFM) check and needs manual review before it can be quoted.'

/** UID-96 (C3) — copy for the struck-through earlier-ready date, verbatim
 * from the prototype's `title` on that span (Fictiv Platform.dc.html line
 * ~1852: `title="Could ship this early — deferred to ship everything
 * together"`). */
const READY_EARLIER_REASON = 'Could ship this early — deferred to ship everything together.'

/** How long a pause in typing must last before the Qty input auto-commits
 * (and triggers server-side recalculation) without requiring blur/Enter. */
const QTY_COMMIT_DEBOUNCE_MS = 500

export interface CartLineRowProps {
  readonly line: LineItem
  /** Server-derived presentation for this same line (thumbnail/part id/
   * material/doc/ship-date/subtotal/volume tiers) — joined to `line` by
   * `sourceableId` at the call site (CartLineTable). Undefined only in a
   * defect state (a line with no matching presentation from the BFF); the
   * row still renders from `line` alone rather than blowing up. */
  readonly presentation?: CartLinePresentation | undefined
  /** UID-411 — the line's real, resolved part thumbnail
   * (`CartViewWithLineSpecifications.lineThumbnails`, joined by
   * `sourceableId` at the call site). `undefined` when the Library has no
   * image for this part — renders the existing placeholder glyph. */
  readonly thumbnailUrl?: string | undefined
  readonly selected: boolean
  /** UID-610 — the raw `click` event (not the resulting boolean), so a
   * shift-click can be resolved into a Gmail/Finder range select by the
   * caller (`CartLineTable.tsx` → `CartPage.tsx`'s `toggleSelected`) via the
   * shared `useRangeSelection` hook. A `ChangeEvent` carries no
   * modifier-key state, so this reads `event.shiftKey` off `Checkbox`'s
   * `onClick` passthrough instead — same UID-600 precedent as
   * `IdentifiedPartRow`/`MatchedPartRow`. */
  readonly onToggleSelected: (event: MouseEvent<HTMLInputElement>) => void
  /** True while a quantity/remove request for this line is in flight — the
   * row disables its controls rather than letting a second request race
   * the first (screens.md "price-recalculating" state). */
  readonly busy?: boolean
  /** When true, show a "From order …" marker under the part name, linking the
   * line to the order it was reordered from (UID-94). The table sets this only
   * when the cart mixes 2+ distinct source orders — in the common single-reorder
   * case the cart-level ReorderSourceNotice already covers it, so the per-line
   * marker would be redundant noise. No-op when the line carries no sourceOrderId. */
  readonly showSourceOrder?: boolean
  /** UID-96 (C3) — the cart's current ship plan mode, and (in ship-together
   * mode) the order-level date the whole shipment goes out on. Defaults to
   * "ship as ready" with no order date, i.e. the pre-C3 behavior (always
   * show the line's own date, never a strike-through) — so existing callers
   * that don't pass these are unaffected. */
  readonly mode?: ShipMode
  readonly orderShipDate?: string | null
  readonly orderShipDateLabel?: string | null
  readonly onQuantityChange: (nextValue: number) => void
  readonly onRemove: () => void
  /** UID-434/WS-E — the line's PERSISTED manufacturing configuration
   * (`CartViewWithLineSpecifications.lineSpecifications`, joined by
   * `sourceableId`), or `undefined` for a line that has never been
   * configuration-edited. Seeds the shared configurator shell's
   * Configuration tab; see {@link fromCartLine}. */
  readonly specification?: CartLineSpecification | undefined
  /** Called with the FULL next cart once a configuration/quantity save made
   * through the "Edit configuration" shell succeeds — the caller (`CartPage`)
   * owns re-rendering from it (subtotal, `lineSpecifications`, etc.), the
   * same way it already does for the row's own quantity stepper and the
   * remove action. */
  readonly onConfigurationSaved: (cart: CartViewWithLineSpecifications) => void
  /** Injectable for tests; defaults to the real BFF-backed
   * `defaultCartLineConfiguratorService`. */
  readonly configuratorService?: CartLineConfiguratorService
}

/**
 * One cart line row (Cart redesign line table): select checkbox, PART
 * (thumbnail + descriptive name + attached-doc chip), REQUIREMENTS (part
 * number + brand/stock or spec line), ship date, a single bordered quantity
 * input (design-fidelity fix — replaces the earlier −/value/+ stepper; see
 * the quantity-input block's own comment), subtotal (+ unit price +
 * volume-pricing disclosure), and a kebab menu (Edit configuration /
 * Duplicate / Remove from cart).
 *
 * PART vs REQUIREMENTS split (matching the rendered design file — the
 * ground-truth cart screen): the PART cell holds only the descriptive name
 * (bold, no part number) and the attached-doc chip; the REQUIREMENTS cell
 * holds the part number (bold, line 1) and the `materialDescription` (gray,
 * line 2 — e.g. "MISUMI · In stock" for a standard part, or a spec string
 * for a custom one). The name never repeats the part number — this app's
 * `CartLinePresentation.partId` arrives as one pre-joined string (e.g.
 * "Deep-groove ball bearing 6800ZZ (B6800ZZ)"); `splitPartIdDisplay` parses
 * it into `{ name, partNumber }` (see its doc for why parsing is required —
 * there is no structured field on the read side).
 *
 * "Edit configuration" opens the SHARED `PartConfiguratorShell` (UID-434/WS-E —
 * supersedes the retired `EditConfigurationModal.tsx`, which only ever round-tripped
 * quantity). Every field `CartLineSpecificationInput` (`apps/api/openapi/schemas/
 * cart.yaml`) can persist — process, material, finish (including the real "as
 * machined" `null` answer), color, threaded holes, inspections, certificates,
 * additional requirements — is now GENUINELY editable and saved via `PATCH
 * /cart/lines/{sourceableId}`, joined to the row's `specification` prop
 * (`CartViewWithLineSpecifications.lineSpecifications`, UID-434 WS-C). A
 * configuration edit that actually changes the stored spec resets the line's price/
 * delivery promise server-side (a price was resolved for the OLD configuration); the
 * shell's own subject-derived pricing footer renders that honestly as "needs
 * pricing" rather than a stale or fabricated figure — see `cart-line-configurator.ts`'s
 * `fromCartLine` doc for exactly how. A pure quantity change never touches the spec
 * object and never resets price (ADR-0032, unchanged). "Remove from cart" is wired to
 * the real `DELETE` endpoint via `onRemove`. (UID-430 removed the row menu's
 * "Duplicate" item — see `RowMenu`'s own doc comment for why.)
 */
export function CartLineRow({
  line,
  presentation,
  thumbnailUrl,
  selected,
  onToggleSelected,
  busy = false,
  showSourceOrder = false,
  mode = 'ship_as_ready',
  orderShipDate = null,
  orderShipDateLabel = null,
  onQuantityChange,
  onRemove,
  specification,
  onConfigurationSaved,
  configuratorService = defaultCartLineConfiguratorService,
}: CartLineRowProps) {
  const [showEditModal, setShowEditModal] = useState(false)
  const readiness = deriveLineReadiness(line, presentation)
  const dfmFail = isDfmFail(presentation)
  const editable = isQuantityEditable(line, presentation) && !busy
  // UID-131 follow-up: render the SERVER-SENT structured labels directly (the presentation now
  // carries distinct `displayName`/`partNumber` — no FE regex-parsing of the joined `partId`).
  // The split is only a fallback for a label-less line / an older feed that sends just `partId`.
  const displayId = presentation?.partId ?? `Item ${line.sourceable.id.slice(0, 8)}`
  const fallback = splitPartIdDisplay(displayId)
  const name = presentation?.displayName ?? fallback.name
  const partNumber = presentation?.partNumber ?? fallback.partNumber
  const currency = presentation?.currency ?? line.price?.currency ?? 'USD'
  // Bug fix (chat report) — a line's persisted `CartLineSpecification` (from
  // `GET /cart`'s `lineSpecifications`) carries process/material/finish/
  // color/tolerance/threaded-holes/inspections/certificates/additional-
  // requirements, none of which the Requirements cell rendered before; it
  // only ever showed `presentation.materialDescription`, a catalog-line
  // "brand · in stock" string that's absent/generic for a configured custom
  // line. Prefer the real specification summary when one exists; fall back
  // to `materialDescription` for a line that's never been configured (the
  // sparse-array "no entry" case `findCartLineSpecification` documents).
  const requirementsDetail =
    formatCartLineSpecificationSummary(
      specification,
      formatProcessLabel,
      formatRequirementsDetail,
    ) ?? presentation?.materialDescription
  const dateDisplay = deriveLineDateDisplay({
    mode,
    presentation,
    orderShipDate,
    orderShipDateLabel,
  })

  // UID-434/WS-E — the shared shell's vertical-agnostic view model + save
  // seam. `subject` is memoized off exactly the inputs `fromCartLine` reads
  // (mirrors the Parts-vertical wrapper's own `useMemo` — the shell's field-
  // reset effect depends on `subject` alone, so a fresh object identity on
  // every render would wrongly look like "a different subject opened").
  const subject = useMemo(
    () => fromCartLine({ line, presentation, specification, displayName: name }),
    [line, presentation, specification, name],
  )
  const saveSeam: PartConfiguratorSaveSeam = useMemo(
    () => ({
      async save(form: PartConfiguratorFormState): Promise<PartConfiguratorSaveOutcome> {
        const specificationPatch = buildCartLineSpecificationPatch(form, specification)
        const quantityChanged = form.quantity !== line.quantity.value

        // UID-403's intent carried forward: never claim to have saved
        // something that didn't actually change — and, structurally, never
        // send an empty PATCH body (the contract's own `minProperties: 1`).
        if (!specificationPatch && !quantityChanged) {
          return { ok: true, subject }
        }

        const outcome = await configuratorService.patch({
          sourceableId: line.sourceable.id,
          ...(quantityChanged
            ? { quantity: { value: form.quantity, uom: line.quantity.uom } }
            : {}),
          ...(specificationPatch ? { specification: specificationPatch } : {}),
        })

        if (!outcome.ok) {
          if (outcome.kind === 'field_errors') {
            return { ok: false, kind: 'field_errors', errors: outcome.errors }
          }
          return { ok: false, kind: 'error', message: outcome.message }
        }

        onConfigurationSaved(outcome.cart)
        const nextLine =
          outcome.cart.lines.find((candidate) => candidate.sourceable.id === line.sourceable.id) ??
          line
        const nextPresentation = outcome.cart.linePresentations.find(
          (candidate) => candidate.sourceableId === line.sourceable.id,
        )
        return {
          ok: true,
          subject: fromCartLine({
            line: nextLine,
            presentation: nextPresentation,
            specification: findCartLineSpecification(outcome.cart, line.sourceable.id),
            displayName: name,
          }),
        }
      },
    }),
    [specification, line, subject, configuratorService, onConfigurationSaved, name],
  )

  // Design-fidelity fix (per the user's rendered design screenshot): a
  // single bordered numeric box, not the −/value/+ stepper. Same
  // empty-string guard as the stepper it replaces (an emptied/cleared input
  // reverts to the last committed value rather than silently clamping to
  // 1). Recalculation still never fires on a half-typed value: blur/Enter
  // commit immediately, and a pause in typing auto-commits after
  // QTY_COMMIT_DEBOUNCE_MS (below) — never on every keystroke.
  const qtyId = useId()
  const qtyErrorId = useId()
  const qtyLockedReasonId = useId()

  /** UID-458 — what (if anything) "Edit configuration" can do for THIS line,
   * derived from its `sourceable.kind` alone (see
   * `resolveCartLineEditAction`). The shared `PartConfiguratorShell` is the
   * CUSTOM-part spec editor, so it only ever opens for `custom-spec`. */
  const editAction = resolveCartLineEditAction(line)
  const [qtyDraft, setQtyDraft] = useState(String(line.quantity.value))
  const [qtyInvalid, setQtyInvalid] = useState(false)
  const qtyCommitTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    setQtyDraft(String(line.quantity.value))
  }, [line.quantity.value])

  function commitQty(next: number) {
    clearTimeout(qtyCommitTimerRef.current)
    setQtyInvalid(false)
    const clamped = Math.min(CART_MAX_LINE_QUANTITY, Math.max(1, Math.round(next)))
    setQtyDraft(String(clamped))
    if (clamped !== line.quantity.value) {
      onQuantityChange(clamped)
    }
  }

  function commitQtyDraft() {
    if (qtyDraft.trim() === '') {
      clearTimeout(qtyCommitTimerRef.current)
      setQtyDraft(String(line.quantity.value))
      setQtyInvalid(true)
      return
    }
    commitQty(Number(qtyDraft))
  }

  // Auto-commit on a pause in typing, so recalculation doesn't require the
  // user to blur the field or press Enter. Guarded against an empty/
  // non-finite draft (left for blur/Enter's revert-to-invalid handling) and
  // against a no-op draft that already matches the committed value.
  // Intentionally keyed ONLY on qtyDraft — commitQty/line.quantity.value are
  // read from the current closure at fire time, and including them would
  // re-run (and restart the debounce timer) on every render instead of only
  // on a typing change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    const draftValue = Number(qtyDraft)
    const isUncommittable =
      qtyDraft.trim() === '' || !Number.isFinite(draftValue) || draftValue === line.quantity.value
    if (isUncommittable) {
      return
    }
    const timer = setTimeout(() => commitQty(draftValue), QTY_COMMIT_DEBOUNCE_MS)
    qtyCommitTimerRef.current = timer
    return () => clearTimeout(timer)
  }, [qtyDraft])

  return (
    <tr className="cart-line-row" data-readiness={readiness} data-dfm-fail={dfmFail}>
      <td className="cart-line-select-cell">
        <Checkbox
          checked={selected}
          onClick={onToggleSelected}
          onChange={() => {}}
          label={<span className="sr-only">Select {name}</span>}
        />
      </td>
      <td className="cart-line-part-cell">
        <div className="cart-line-part-row">
          {/* UID-282: the revision caption sits BELOW the thumbnail box per the
              comp (`.dc.html:2221`), so it is a sibling of it, not a child. `rev`
              rides on the line's own SourceableRef (no CartLinePresentation change
              needed) and is present only for custom parts — catalog lines render
              nothing here, leaving the cell exactly as it was. */}
          <div className="cart-part-thumb-wrap">
            <PartThumbnail url={thumbnailUrl} />
            <PartRevBand rev={line.sourceable.rev} variant="caption" />
          </div>
          <div className="cart-line-part-info">
            <p className="cart-line-name">{name}</p>
            {presentation?.attachedDocName && (
              <span className="cart-line-doc-chip">
                <DocIcon />
                <span className="cart-line-doc-chip-name" title={presentation.attachedDocName}>
                  {presentation.attachedDocName}
                </span>
              </span>
            )}
            {showSourceOrder && presentation?.sourceOrderId && (
              <p className="cart-line-reorder-note">
                From{' '}
                <Link
                  to={`/orders/${presentation.sourceOrderId}`}
                  className="cart-reorder-notice-link"
                >
                  order {formatOrderRef(presentation.sourceOrderId)}
                </Link>
              </p>
            )}
            <div className="cart-line-callouts">
              {readiness === 'quote-required' && (
                <Chip tone="purple" icon={<LockOrQuoteIcon kind="quote" />}>
                  Quote required
                </Chip>
              )}
              {readiness === 'locked' && (
                <Tooltip label={LOCKED_QUANTITY_REASON}>
                  <Chip tone="warning" icon={<LockOrQuoteIcon kind="lock" />}>
                    Locked to quote
                  </Chip>
                </Tooltip>
              )}
              {dfmFail && (
                <Tooltip label={DFM_FAIL_REASON}>
                  <Chip tone="error" icon={<LockOrQuoteIcon kind="quote" />}>
                    Review required (DFM)
                  </Chip>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="cart-line-requirements-cell">
        {partNumber && <p className="cart-line-requirements-number">{partNumber}</p>}
        {requirementsDetail && <p className="cart-line-material">{requirementsDetail}</p>}
      </td>
      <td className="cart-line-ship-date-cell">
        {/* Flex column so the struck-through "could-ship-earlier" date stacks
            BELOW the primary date (design: `flex-direction:column;gap:1px`),
            rather than flowing inline after it (the Tooltip wrapper is inline,
            which otherwise put the two dates side-by-side and overflowed the
            struck date into the Qty column). The <td> itself can't be the flex
            container without dropping its table-cell sizing. */}
        <div className="cart-line-ship-date-stack">
          <span className="cart-line-ship-date">{dateDisplay.primaryDateLabel ?? '—'}</span>
          {dateDisplay.earlyReadyDateLabel && (
            // Code-review S3: this is a non-interactive annotation (the earlier
            // date the line COULD have shipped on), not a control — a <button>
            // wrongly announced a dead, actionable element to AT. Render a plain
            // <span> instead; the Tooltip still provides the visual hover
            // affordance for pointer users (its onMouseEnter/Leave work on any
            // element), and an always-present sr-only clause exposes the same
            // explanation to screen-reader users inline (not gated on focus,
            // which a non-focusable span can't receive).
            <Tooltip label={READY_EARLIER_REASON}>
              <span className="cart-line-ready-early-date">
                {dateDisplay.earlyReadyDateLabel}
                <span className="sr-only"> — {READY_EARLIER_REASON}</span>
              </span>
            </Tooltip>
          )}
        </div>
      </td>
      <td className="cart-line-qty-cell">
        <input
          id={qtyId}
          // UID-570 — dropped `type="number"` (kept `inputMode="numeric"`):
          // the native spinner ate space on one side, visually skewing the
          // centered digit off-center, same defect fixed on RFQ's Qty input.
          // The real floor/ceiling enforcement is `commitQty`'s
          // `Math.min`/`Math.max` clamp above, not these now-inert
          // `min`/`max` attributes (harmless leftovers on a text input).
          inputMode="numeric"
          min={1}
          max={CART_MAX_LINE_QUANTITY}
          className="cart-line-qty-input"
          aria-label={`Quantity for ${name}`}
          aria-invalid={qtyInvalid}
          aria-describedby={
            qtyInvalid ? qtyErrorId : readiness === 'locked' ? qtyLockedReasonId : undefined
          }
          disabled={!editable}
          value={qtyDraft}
          onChange={(event) => setQtyDraft(event.target.value)}
          onBlur={commitQtyDraft}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commitQtyDraft()
            }
          }}
        />
        {qtyInvalid && (
          <p id={qtyErrorId} role="alert" className="form-error-text cart-line-qty-error">
            Enter a quantity — reverted to {line.quantity.value}.
          </p>
        )}
        {/* Exposed to assistive tech even though the Tooltip on the "Locked to
            quote" chip already explains this visually (WCAG F1) — a screen-
            reader user landing directly on the disabled input still needs the
            reason, not just that it's unavailable. */}
        {readiness === 'locked' && (
          <span id={qtyLockedReasonId} className="sr-only">
            {LOCKED_QUANTITY_REASON}
          </span>
        )}
      </td>
      <td className="cart-line-subtotal-cell">
        {presentation?.lineSubtotalAmount ? (
          <>
            <p className="cart-line-subtotal">
              {formatMoney(presentation.lineSubtotalAmount, currency)}
            </p>
            {presentation.unitPriceAmount && (
              <p className="cart-line-unit-price">
                {formatMoney(presentation.unitPriceAmount, currency)} ea
              </p>
            )}
            {presentation.volumeTiers.length > 0 && (
              <VolumeDiscountDisclosure tiers={presentation.volumeTiers} currency={currency} />
            )}
          </>
        ) : (
          <p className="cart-line-pending-price">Pending quote</p>
        )}
      </td>
      <td className="cart-line-actions-cell">
        <RowMenu
          name={name}
          busy={busy}
          onRemove={onRemove}
          /* UID-458 — WHICH "Edit configuration" affordance this line gets is
             decided from its `sourceable.kind` here, at the row level, so
             `RowMenu` stays purely presentational (it renders a resolved
             action, it doesn't branch on cart-domain kinds itself). */
          editAction={editAction}
          onEditConfiguration={() => setShowEditModal(true)}
        />
        {/* Rendered inside a <td> (not a direct <tr> child) — a <div>-based overlay
            (Modal has no portal) is only valid HTML nested inside table cell content;
            its fixed positioning still escapes the table visually either way.
            UID-434/WS-E: the shared configurator shell replaces the retired
            `EditConfigurationModal` — see this file's own module doc for what it
            now genuinely persists. */}
        {/* UID-458: mounted only for the custom-spec path. `showEditModal`
            can only ever be set from that path's own menu item, so this is
            belt-and-braces — but it also keeps the shell (and its 3D-viewer
            dynamic import) entirely out of a catalog/assembly row's tree. */}
        {editAction.kind === 'custom-spec' && (
          <PartConfiguratorShell
            open={showEditModal}
            onClose={() => setShowEditModal(false)}
            subject={subject}
            capabilities={CART_CONFIGURATOR_CAPABILITIES}
            saveSeam={saveSeam}
          />
        )}
      </td>
    </tr>
  )
}

/**
 * The row's kebab menu. A real `<button>` trigger + a managed-state popup
 * (not a native `<details>/<summary>`) — `<summary>` has no standard
 * "button" ARIA mapping, so a hand-rolled `role="button"` on it would be
 * exactly the anti-pattern the a11y skill warns against ("prefer native
 * elements over div/summary+role"); a real `<button>` gets that semantics
 * for free.
 *
 * DROPDOWN-CLIPPING FIX: the line table now lives inside `.cart-line-
 * table-card`, which uses `overflow: hidden` to clip its rounded corners
 * (design-fidelity fix, an earlier pass) — that same `overflow: hidden`
 * was ALSO clipping this menu's own open panel (and, depending on scroll
 * position, the trigger itself) to the card's bounds, a real functional
 * bug (an unreachable "Remove from cart"/"Edit configuration").
 * Fixed the same way `Modal.tsx` escapes its own ancestors: portal the open
 * panel to `document.body` (`position: fixed`, positioned from the
 * trigger's own `getBoundingClientRect()` so it still visually anchors
 * under the trigger) rather than removing the card's corner-clipping.
 *
 * Portaling means the panel is no longer a DOM descendant of this
 * component's own wrapper, so the previous `onBlur` + `relatedTarget`
 * outside-click check (which relies on DOM containment) no longer detects
 * clicks inside the panel. Positioning + outside-click/Escape/scroll-close
 * wiring is shared with `VolumeDiscountDisclosure` below (the SAME
 * clipping bug, same fix shape) via `useAnchoredPopover` — see that hook's
 * doc for the full rationale.
 */
function RowMenu({
  name,
  busy,
  onRemove,
  editAction,
  onEditConfiguration,
}: {
  readonly name: string
  readonly busy: boolean
  readonly onRemove: () => void
  /** UID-458 — the ALREADY-RESOLVED meaning of "Edit configuration" for this
   * line (`resolveCartLineEditAction`, called by the row). This component
   * stays presentational: it renders whichever affordance the action names
   * and never inspects `sourceable.kind` itself. */
  readonly editAction: CartLineEditAction
  readonly onEditConfiguration: () => void
}) {
  const [open, setOpen] = useState(false)
  const editReasonId = useId()
  const { triggerRef, panelRef, position } = useAnchoredPopover(open, setOpen)

  return (
    <div className="cart-line-menu">
      <button
        ref={triggerRef}
        type="button"
        className="cart-line-menu-trigger"
        aria-label={`More actions for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <KebabIcon />
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            className="cart-line-menu-panel"
            role="menu"
            style={{ top: position.top, right: position.right }}
          >
            {editAction.kind === 'unavailable' ? (
              <>
                {/* WCAG F1 (the SAME pattern as the locked-quantity input
                    above): `aria-disabled` + a real, focusable `<button>`
                    rather than the native `disabled` attribute — a natively
                    disabled control leaves the a11y tree and the tab order
                    entirely, so a keyboard/screen-reader user would find the
                    action simply MISSING and never learn why. The reason
                    below is a single source of truth: visible to sighted
                    users AND wired to this item via `aria-describedby`. */}
                <button
                  type="button"
                  role="menuitem"
                  className="cart-line-menu-item"
                  aria-disabled="true"
                  aria-describedby={editReasonId}
                  onClick={(event) => {
                    // Neither edit nor close: the menu stays open so the
                    // reason stays on screen for the user who just clicked.
                    event.preventDefault()
                  }}
                >
                  <MenuEditIcon />
                  Edit configuration
                </button>
                {/* Deliberately a SIBLING of the button, not a child — nesting
                    it would fold the reason into the item's accessible NAME
                    ("Edit configuration <reason>") instead of its description. */}
                <p id={editReasonId} className="cart-line-menu-item-reason">
                  {editAction.reason}
                </p>
              </>
            ) : editAction.kind === 'custom-spec' ? (
              <button
                type="button"
                role="menuitem"
                className="cart-line-menu-item"
                onClick={() => {
                  setOpen(false)
                  onEditConfiguration()
                }}
              >
                <MenuEditIcon />
                Edit configuration
              </button>
            ) : // `catalog-configurable` — reserved for UID-459 (reopen the
            // real Catalog configuration UI, pre-seeded with this line's
            // picks). `resolveCartLineEditAction` never returns it yet, and
            // rendering NOTHING is the honest interim: an enabled item here
            // would route a configurable part into the custom-spec shell,
            // the very bug UID-458 removes.
            null}
            <hr className="cart-line-menu-divider" />
            <button
              type="button"
              role="menuitem"
              className="cart-line-menu-item cart-line-menu-item-danger"
              disabled={busy}
              onClick={() => {
                setOpen(false)
                onRemove()
              }}
            >
              <MenuTrashIcon />
              Remove from cart
            </button>
          </div>,
          document.body,
        )}
    </div>
  )
}

/**
 * DROPDOWN-CLIPPING FIX (same root cause + fix shape as `RowMenu`'s doc):
 * this was a native `<details>/<summary>` — its absolutely-positioned panel
 * was clipped by `.cart-line-table-card`'s `overflow: hidden` exactly like
 * the row's kebab menu was. A native `<details>` can't be portaled (its
 * show/hide behavior is native-DOM-structure-coupled, not something
 * `createPortal` can lift out while keeping the toggle semantics), so this
 * is now a real `<button>` trigger + managed-state panel, portaled to
 * `document.body` the same way `RowMenu` is — position computed from the
 * trigger's own `getBoundingClientRect()`, closes on Escape/outside-click/
 * scroll/resize. Tier content and data are unchanged.
 */
function VolumeDiscountDisclosure({
  tiers,
  currency,
}: {
  readonly tiers: readonly components['schemas']['VolumePricingTier'][]
  readonly currency: string
}) {
  const [open, setOpen] = useState(false)
  const { triggerRef, panelRef, position } = useAnchoredPopover(open, setOpen)

  return (
    <div className="cart-line-price-detail">
      <button
        ref={triggerRef}
        type="button"
        className="cart-line-price-detail-trigger"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((current) => !current)}
      >
        Volume pricing
        <ChevronDownIcon />
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            className="cart-line-price-detail-panel"
            style={{ top: position.top, right: position.right }}
          >
            <p className="volume-tier-heading">Volume pricing</p>
            <ul className="volume-tier-list">
              {tiers.map((tier) => (
                <li key={tier.minQuantity} className="volume-tier-row">
                  <span>{tier.minQuantity}+</span>
                  <span>{formatMoney(tier.unitPriceAmount, currency)}</span>
                  <span className="volume-tier-off">{tier.pctOff}% off</span>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  )
}

function LockOrQuoteIcon({ kind }: { readonly kind: 'lock' | 'quote' }) {
  return kind === 'lock' ? (
    <span aria-hidden="true">&#128274;</span>
  ) : (
    <span aria-hidden="true">&#9993;</span>
  )
}

function DocIcon() {
  return (
    <svg
      aria-hidden="true"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  )
}

/** Row-menu icons — Lucide stroke glyphs matching the rendered design
 * (square-pen / copy / trash-2), replacing the Ant filled icons that read as
 * a different set. Sized/coloured by `.cart-line-menu-item svg` (cart.css). */
function MenuEditIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
    </svg>
  )
}

function MenuTrashIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </svg>
  )
}

/** Down chevron after "Volume pricing" (design shows a grey disclosure caret,
 * not a bare link). */
function ChevronDownIcon() {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

function KebabIcon() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  )
}
